import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LABEL = "text-xs uppercase tracking-[0.1em] text-[#6B7280]";

/** mode: "phone" (OTP sign-in) or "reset" (forgot password) */
export const OtpAuthPanel = ({ mode, onBack }) => {
  const { applyUser } = useAuth();
  const [codes, setCodes] = useState([]);
  const [dial, setDial] = useState("+91");
  const [local, setLocal] = useState("");
  const [email, setEmail] = useState("");
  const [step, setStep] = useState("identify");
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [sentTo, setSentTo] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [via, setVia] = useState(mode === "phone" ? "phone" : "email");

  useEffect(() => {
    api.get("/meta/country-codes")
      .then(({ data }) => setCodes(data.countries || []))
      .catch(() => setCodes([{ iso: "IN", name: "India", dial_code: "+91" }]));
  }, []);

  const identifier = via === "phone" ? `${dial}${local.replace(/\D/g, "")}` : email.trim();
  const purpose = mode === "reset" ? "reset" : "login";

  const request = async (e) => {
    e?.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/otp/request", { identifier, purpose });
      setSentTo(data.sent_to || "your registered email");
      setStep("code");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/otp/verify", { identifier, code: otp, purpose });
      if (purpose === "reset") { setResetToken(data.reset_token); setStep("newpw"); }
      else await applyUser(data);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset-password", { reset_token: resetToken, new_password: pw });
      setStep("done");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const err = error && <p className="text-sm text-[#C92A2A]" data-testid="otp-error">{error}</p>;
  const back = (
    <button type="button" onClick={onBack} data-testid="otp-back-btn"
      className="text-sm text-[#002FA7] hover:underline">Back to sign in</button>
  );

  if (step === "done") {
    return (
      <div className="space-y-4" data-testid="otp-reset-done">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-[#111827]">Password updated</h2>
        <p className="text-sm text-[#6B7280]">You can now sign in with your new password.</p>
        {back}
      </div>
    );
  }

  if (step === "newpw") {
    return (
      <form onSubmit={savePw} className="space-y-4" data-testid="otp-newpw-form">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-[#111827]">Set a new password</h2>
        <div>
          <Label className={LABEL}>New password</Label>
          <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8}
            className="mt-1.5" data-testid="otp-newpw-input" />
        </div>
        {err}
        <Button type="submit" disabled={busy} data-testid="otp-newpw-submit"
          className="w-full bg-[#002FA7] hover:bg-[#00237C] text-white">
          {busy ? "Saving…" : "Save password"}
        </Button>
        {back}
      </form>
    );
  }

  if (step === "code") {
    return (
      <form onSubmit={verify} className="space-y-4" data-testid="otp-code-form">
        <h2 className="font-heading text-2xl font-bold tracking-tight text-[#111827]">Enter your code</h2>
        <p className="text-sm text-[#6B7280]">
          We emailed a 6-digit code to <b data-testid="otp-sent-to">{sentTo}</b>. It expires in 10 minutes.
        </p>
        <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="123456" autoFocus required
          inputMode="numeric" maxLength={6} data-testid="otp-code-input" />
        {err}
        <Button type="submit" disabled={busy} data-testid="otp-verify-btn"
          className="w-full bg-[#002FA7] hover:bg-[#00237C] text-white">
          {busy ? "Verifying…" : purpose === "reset" ? "Verify code" : "Verify and sign in"}
        </Button>
        <div className="flex items-center justify-between">
          <button type="button" onClick={request} disabled={busy} data-testid="otp-resend-btn"
            className="text-sm text-[#002FA7] hover:underline">Resend code</button>
          {back}
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={request} className="space-y-4" data-testid="otp-request-form">
      <h2 className="font-heading text-2xl font-bold tracking-tight text-[#111827]">
        {mode === "reset" ? "Forgot password" : "Sign in with your number"}
      </h2>
      <p className="text-sm text-[#6B7280]">
        {mode === "reset"
          ? "Tell us how to find your account. The reset code always goes to your registered email."
          : "We will email a one-time code to the address registered against this number."}
      </p>

      {mode === "reset" && (
        <div className="flex gap-2" data-testid="otp-via-switch">
          {["email", "phone"].map((v) => (
            <button key={v} type="button" onClick={() => setVia(v)} data-testid={`otp-via-${v}`}
              className={`flex-1 rounded-md border px-3 py-1.5 text-xs uppercase tracking-[0.1em] ${
                via === v ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-[#E5E7EB] text-[#6B7280]"}`}>
              {v === "email" ? "Email" : "Phone"}
            </button>
          ))}
        </div>
      )}

      {via === "phone" ? (
        <div>
          <Label className={LABEL}>Contact number</Label>
          <div className="mt-1.5 flex gap-2">
            <select value={dial} onChange={(e) => setDial(e.target.value)} data-testid="otp-country-select"
              className="w-32 rounded-md border border-[#E5E7EB] bg-white px-2 py-2 text-sm">
              {codes.map((c) => (
                <option key={c.iso} value={c.dial_code}>{`${c.name} (${c.dial_code})`}</option>
              ))}
            </select>
            <Input value={local} onChange={(e) => setLocal(e.target.value)} required inputMode="tel"
              placeholder="98765 43210" data-testid="otp-phone-input" />
          </div>
        </div>
      ) : (
        <div>
          <Label className={LABEL}>Email</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            className="mt-1.5" data-testid="otp-email-input" />
        </div>
      )}
      {err}
      <Button type="submit" disabled={busy} data-testid="otp-request-btn"
        className="w-full bg-[#002FA7] hover:bg-[#00237C] text-white">
        {busy ? "Sending…" : "Send code"}
      </Button>
      {back}
    </form>
  );
};
